import { useState, useEffect, useRef } from "react";

const BASE = "https://support.gs-group.fr/api";

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function Canvas({ onSave }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSign, setHasSign] = useState(false);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0369a1";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const getPos = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: (t.clientX - r.left) * (canvas.width / r.width), y: (t.clientY - r.top) * (canvas.height / r.height) };
  };

  const start = e => { e.preventDefault(); setDrawing(true); setHasSign(true); lastPos.current = getPos(e, ref.current); };
  const move  = e => {
    e.preventDefault();
    if (!drawing) return;
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y); ctx.lineTo(pos.x, pos.y); ctx.stroke();
    lastPos.current = pos;
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSign(false);
  };

  const save = () => {
    if (!hasSign) return;
    onSave(ref.current.toDataURL("image/png"), hasSign);
  };

  return (
    <div>
      <div style={{ background:"#f0f9ff", borderRadius:12, border:"2px dashed #7dd3fc", overflow:"hidden", touchAction:"none", marginBottom:12 }}>
        <canvas ref={ref} width={560} height={180}
          style={{ display:"block", width:"100%", height:180, touchAction:"none", cursor:"crosshair" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}/>
      </div>
      {!hasSign && <p style={{ textAlign:"center", color:"#94a3b8", fontSize:13, margin:"0 0 12px" }}>Signez ici avec votre doigt ou votre souris</p>}
      <div style={{ display:"flex", gap:10 }}>
        <button onClick={clear}
          style={{ flex:1, padding:"12px", borderRadius:10, background:"#fff", border:"1px solid #e2e8f0", color:"#64748b", fontSize:14, fontWeight:600, cursor:"pointer" }}>
          🗑️ Effacer
        </button>
        <button onClick={save} disabled={!hasSign}
          style={{ flex:2, padding:"12px", borderRadius:10, background:hasSign?"linear-gradient(135deg,#0369a1,#0284c7)":"#e2e8f0", border:"none", color:hasSign?"#fff":"#94a3b8", fontSize:14, fontWeight:700, cursor:hasSign?"pointer":"not-allowed" }}>
          ✅ Valider ma signature
        </button>
      </div>
    </div>
  );
}

export default function SignaturePublique({ token }) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState("");
  const [step, setStep]     = useState("loading"); // loading | ready | form | signed | already
  const [nom, setNom]       = useState("");
  const [nomErr, setNomErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/sign/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(d.error); setStep("error"); return; }
        setData(d);
        setStep(d.signature_client_at ? "already" : "ready");
      })
      .catch(() => { setError("Impossible de charger le document."); setStep("error"); });
  }, [token]);

  const handleSign = async (sigUrl) => {
    if (!nom.trim()) { setNomErr("Veuillez saisir votre nom complet avant de signer."); return; }
    setNomErr("");
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_url: sigUrl, client_nom: nom.trim() }),
      });
      const res = await r.json();
      if (res.ok) setStep("signed");
      else setError(res.error || "Erreur");
    } catch { setError("Erreur réseau"); }
    setSaving(false);
  };

  if (step === "loading") return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f0f9ff" }}>
      <div style={{ textAlign:"center", color:"#0369a1" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>⚡</div>
        <div style={{ fontSize:16, fontWeight:600 }}>Chargement du document…</div>
      </div>
    </div>
  );

  if (step === "error") return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#fff1f2" }}>
      <div style={{ textAlign:"center", maxWidth:400, padding:32 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>⛔</div>
        <div style={{ fontSize:18, fontWeight:700, color:"#991b1b", marginBottom:8 }}>Lien invalide</div>
        <div style={{ fontSize:14, color:"#6b7280" }}>{error}</div>
      </div>
    </div>
  );

  if (step === "already") return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f0fdf4" }}>
      <div style={{ textAlign:"center", maxWidth:440, padding:32 }}>
        <div style={{ fontSize:56, marginBottom:16 }}>✅</div>
        <div style={{ fontSize:20, fontWeight:800, color:"#065f46", marginBottom:8 }}>Document déjà signé</div>
        <div style={{ fontSize:14, color:"#64748b", lineHeight:1.6 }}>
          Ce procès-verbal a été signé le {fmt(data?.signature_client_at)}
          {data?.signature_client_nom && ` par ${data.signature_client_nom}`}.
        </div>
        <div style={{ marginTop:20, padding:"16px 20px", background:"#d1fae5", border:"1px solid #6ee7b7", borderRadius:10, fontSize:13, color:"#065f46" }}>
          Merci pour votre confiance. GS GROUP vous recontactera si nécessaire.
        </div>
      </div>
    </div>
  );

  if (step === "signed") return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f0fdf4" }}>
      <div style={{ textAlign:"center", maxWidth:440, padding:32 }}>
        <div style={{ fontSize:60, marginBottom:16 }}>🎉</div>
        <div style={{ fontSize:22, fontWeight:900, color:"#065f46", marginBottom:10 }}>Signature validée !</div>
        <div style={{ fontSize:14, color:"#64748b", lineHeight:1.7 }}>
          Votre procès-verbal d'installation a bien été signé.<br/>
          Un exemplaire est conservé dans nos dossiers.
        </div>
        <div style={{ marginTop:24, padding:"16px 20px", background:"#d1fae5", border:"1px solid #6ee7b7", borderRadius:10, fontSize:13, color:"#065f46" }}>
          Merci de votre confiance. Pour toute question contactez GS GROUP.
        </div>
      </div>
    </div>
  );

  // step === "ready"
  const dateInstall = data.date_installation || data.date_planifiee;

  return (
    <div style={{ minHeight:"100vh", background:"#f8fafc", display:"flex", flexDirection:"column" }}>

      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#0369a1,#0284c7)", padding:"24px 32px", color:"#fff" }}>
        <div style={{ maxWidth:680, margin:"0 auto", display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ fontSize:36 }}>⚡</div>
          <div>
            <div style={{ fontSize:20, fontWeight:900 }}>GS GROUP — Signature PV</div>
            <div style={{ fontSize:13, opacity:.85, marginTop:2 }}>Procès-verbal d'installation à signer en ligne</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:680, margin:"0 auto", padding:"32px 24px", width:"100%", flex:1 }}>

        {/* Récap intervention */}
        <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"22px 24px", marginBottom:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".5px", marginBottom:12 }}>Détails de l'installation</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px 20px" }}>
            {[
              ["Intervention", data.titre],
              ["Client",       data.client_nom],
              ["Technicien",   data.technicien_nom],
              ["Date",         fmt(dateInstall)],
            ].map(([k,v]) => (
              <div key={k}>
                <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600 }}>{k}</div>
                <div style={{ fontSize:14, fontWeight:700, color:"#1e293b", marginTop:2 }}>{v || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Nom signataire */}
        <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"22px 24px", marginBottom:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".5px", marginBottom:12 }}>Signataire</div>
          <input
            value={nom}
            onChange={e => { setNom(e.target.value); setNomErr(""); }}
            placeholder="Nom et prénom du signataire *"
            style={{ width:"100%", padding:"11px 14px", borderRadius:10, border:`1.5px solid ${nomErr?"#ef4444":"#e2e8f0"}`, fontSize:14, outline:"none", color:"#1e293b", background:"#f8fafc", boxSizing:"border-box" }}
          />
          {nomErr && <div style={{ fontSize:12, color:"#ef4444", marginTop:6 }}>⚠ {nomErr}</div>}
        </div>

        {/* Zone de signature */}
        <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"22px 24px", marginBottom:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".5px", marginBottom:14 }}>✍️ Votre signature</div>
          {saving
            ? <div style={{ textAlign:"center", padding:"40px 0", color:"#0369a1", fontWeight:600 }}>Enregistrement…</div>
            : <Canvas onSave={handleSign} />
          }
        </div>

        {/* Mentions légales */}
        <div style={{ fontSize:12, color:"#94a3b8", textAlign:"center", lineHeight:1.6 }}>
          En signant, vous confirmez la bonne réalisation de l'installation décrite ci-dessus.<br/>
          La signature numérique est légalement reconnue conformément aux CGV GS GROUP.
        </div>
      </div>
    </div>
  );
}
